// screens/HomeScreen.js
// BuzzInApp — Lista de comercios ordenada por distancia real
//
// Mejoras v2:
//   • Banner de GPS inline cuando la ubicación está pendiente o denegada
//   • Título con gradiente sutil acento-marca
//   • Contador de resultados ("X comercios cerca")
//   • Focus ring en buscador con glow naranja
//   • Filtro activo con font-weight 600

import React, { useState, useMemo } from "react";
import useBusinesses from "../hooks/useBusinesses";
import useGeolocation from "../hooks/useGeolocation";
import useClicks from "../hooks/useClicks";
import BizCard from "../components/BizCard";
import LevelBar from "../components/LevelBar";
import useTranslation from "../i18n/useTranslation";
import "./HomeScreen.css";

// ─── Ícono de búsqueda ────────────────────────────────────────────────────────
const IconSearch = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
    <circle cx="11" cy="11" r="7" stroke="var(--text-muted)" strokeWidth="1.8"/>
    <path d="M20 20L16.65 16.65" stroke="var(--text-muted)" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

// ─── BizCard conectada con sus propios clicks ─────────────────────────────────
const BizCardWithClicks = ({ business, uid, onShare, onClaim }) => {
  const { clickCount, canClaim } = useClicks(uid, business);
  return (
    <BizCard
      business={business}
      clickCount={clickCount}
      canClaim={canClaim}
      onShare={() => onShare(business)}
      onClaim={() => onClaim(business)}
    />
  );
};

// ─── HomeScreen ───────────────────────────────────────────────────────────────
const HomeScreen = ({ authHook, onNavigate }) => {
  const { t } = useTranslation();
  const { user, profile } = authHook;

  const [searchQuery, setSearchQuery] = useState("");
  const [activeType, setActiveType]  = useState(null);

  const geo = useGeolocation();
  const { businesses, loading, error, refresh } = useBusinesses(geo.coords);

  // Tipos únicos para filtros
  const types = useMemo(() => {
    const all = businesses.map((b) => b.type).filter(Boolean);
    return [...new Set(all)];
  }, [businesses]);

  // Filtrado
  const filtered = useMemo(() => {
    let list = businesses;
    if (activeType) {
      list = list.filter(
        (b) => b.type?.toLowerCase() === activeType.toLowerCase()
      );
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (b) =>
          b.name?.toLowerCase().includes(q) ||
          b.type?.toLowerCase().includes(q) ||
          b.address?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [businesses, activeType, searchQuery]);

  const handleShare = (business) => onNavigate("share", { business });
  const handleClaim = (business) => onNavigate("claim", { business });

  // ─── Estado del GPS para el banner ───────────────────────────────────────
  const showGeoBanner = !loading && (geo.loading || !geo.available);
  const geoBannerError = !geo.available && !geo.loading;

  return (
    <div className="home-screen">
      {/* Header sticky */}
      <header className="home-screen__header">
        <div className="home-screen__title-row">
          <div>
            <h1 className="home-screen__title">{t("home.title")}</h1>
            <p className="home-screen__subtitle">{t("home.subtitle")}</p>
          </div>
          {profile && (
            <div className="home-screen__level">
              <LevelBar totalClicks={profile.totalClicks ?? 0} compact />
            </div>
          )}
        </div>

        {/* Banner de GPS — solo cuando hay problema con la ubicación */}
        {showGeoBanner && (
          <div
            className={`home-screen__geo-banner ${geoBannerError ? "home-screen__geo-banner--error" : ""}`}
            role="status"
          >
            <div className="home-screen__geo-dot" aria-hidden="true" />
            {geo.loading
              ? "Obteniendo tu ubicación para ordenar por distancia…"
              : "Activá la ubicación para ver comercios cerca tuyo"
            }
          </div>
        )}

        {/* Buscador */}
        <div className="home-screen__search-wrap">
          <IconSearch />
          <input
            className="home-screen__search"
            type="search"
            placeholder={t("home.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label={t("home.searchPlaceholder")}
          />
        </div>

        {/* Filtros */}
        {types.length > 0 && (
          <div className="home-screen__filters" role="list" aria-label="Filtrar por categoría">
            <button
              className={`home-screen__filter-btn ${!activeType ? "home-screen__filter-btn--active" : ""}`}
              onClick={() => setActiveType(null)}
              aria-pressed={!activeType}
            >
              {t("home.filterAll")}
            </button>
            {types.map((type) => (
              <button
                key={type}
                className={`home-screen__filter-btn ${activeType === type ? "home-screen__filter-btn--active" : ""}`}
                onClick={() => setActiveType(activeType === type ? null : type)}
                aria-pressed={activeType === type}
              >
                {type}
              </button>
            ))}
          </div>
        )}
      </header>

      {/* Lista */}
      <main className="home-screen__list">
        {loading && (
          <div className="home-screen__loading" aria-live="polite">
            {[1, 2, 3].map((i) => (
              <div key={i} className="home-screen__skeleton" aria-hidden="true" />
            ))}
          </div>
        )}

        {error && !loading && (
          <div className="home-screen__error" role="alert">
            <p>{t("errors.generic")}</p>
            <button className="home-screen__retry-btn" onClick={refresh}>
              {t("app.retry")}
            </button>
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="home-screen__empty">
            <p className="home-screen__empty-title">{t("home.noResults")}</p>
            <p className="home-screen__empty-hint">{t("home.noResultsHint")}</p>
          </div>
        )}

        {/* Contador de resultados */}
        {!loading && !error && filtered.length > 0 && (
          <p className="home-screen__count" aria-live="polite">
            {filtered.length} {filtered.length === 1 ? "comercio" : "comercios"}
            {activeType ? ` · ${activeType}` : ""}
          </p>
        )}

        {!loading && !error && filtered.map((business) => (
          <BizCardWithClicks
            key={business.id}
            business={business}
            uid={user?.uid}
            onShare={handleShare}
            onClaim={handleClaim}
          />
        ))}
      </main>
    </div>
  );
};

export default HomeScreen;